import { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { FaCloudUploadAlt } from "react-icons/fa";

function ServiceCategoryModal({
  show,
  handleClose,
  fetchServiceCatData,
  service,
}) {
  const [formData, setFormData] = useState({
    serviceCategoryName: "",
    service_cat_descriptions: "",
    service_cat_image: null,
    imagePreviewUrl: service ? service.service_cat_image : null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    if (service) {
      setFormData({
        serviceCategoryName: service.service_category_name || "",
        service_cat_descriptions: service.service_cat_descriptions || "",
        service_cat_image: service.service_cat_image || null,
        imagePreviewUrl: service.service_cat_image || null,
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "service_cat_image") {
      const file = files[0];
      if (file && file.type.startsWith("image/")) {
        setFormData({
          ...formData,
          service_cat_image: file,
          imagePreviewUrl: URL.createObjectURL(file),
        });
        setErrors({ ...errors, service_cat_image: null });
      } else {
        toast.error("Please upload a valid image file");
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleIconClick = () => {
    inputRef.current.click();
  };

  const validateFields = () => {
    let fieldErrors = {};
    if (!formData.serviceCategoryName) {
      fieldErrors.serviceCategoryName = "Service Category Name is required.";
    }
    if (!formData.service_cat_descriptions) {
      fieldErrors.service_cat_descriptions =
        "Service Category Description is required.";
    }
    return fieldErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = new FormData();
    payload.append("service_category_name", formData.serviceCategoryName);
    payload.append(
      "service_cat_descriptions",
      formData.service_cat_descriptions,
    );
    if (
      formData.service_cat_image &&
      typeof formData.service_cat_image !== "string"
    ) {
      payload.append("service_cat_image", formData.service_cat_image);
    }
    setLoading(true);

    try {
      let response;
      if (service) {
        response = await axios.put(
          `${BASE_URL}/api/service-categories/${service.id}/`,
          payload,
          {
            headers: {
              ...HEADERS,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        toast.success(
          response.data.message || "Service Category updated successfully",
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/api/service-categories/`,
          payload,
          {
            headers: {
              ...HEADERS,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        toast.success(
          response.data.message || "Service Category added successfully",
        );
      }
      handleClose();
      fetchServiceCatData();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {service ? "Edit Service Category" : "Add Service Category"}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <Form.Floating className="mb-3">
              <Form.Control
                type="text"
                id="serviceCategoryName"
                name="serviceCategoryName"
                placeholder="Service Category Name"
                value={formData.serviceCategoryName}
                onChange={handleChange}
                isInvalid={!!errors.serviceCategoryName}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.serviceCategoryName}
              </Form.Control.Feedback>
              <label htmlFor="serviceCategoryName">
                Service Category Name*
              </label>
            </Form.Floating>

            <Row className="align-items-center">
              <Col xs={12} md={4} className="text-center mb-3 mb-md-0">
                <div
                  className="border rounded p-3 d-flex flex-column align-items-center"
                  style={{ height: "150px", cursor: "pointer" }}
                  onClick={handleIconClick}
                >
                  <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label className="d-flex flex-column align-items-center justify-content-center">
                      {formData.imagePreviewUrl ? (
                        <img
                          src={formData.imagePreviewUrl}
                          alt="Service"
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{ height: "100px" }}
                        >
                          <FaCloudUploadAlt size={50} />
                          <span>Upload Image</span>
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control-file d-none"
                        name="service_cat_image"
                        onChange={handleChange}
                        ref={inputRef}
                      />
                    </Form.Label>
                  </Form.Group>
                  {errors.service_cat_image && (
                    <div className="text-danger">
                      {errors.service_cat_image}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={12} md={8}>
                <Form.Floating>
                  <Form.Control
                    as="textarea"
                    id="service_cat_descriptions"
                    name="service_cat_descriptions"
                    placeholder="Service Category Description"
                    style={{ height: "100px" }}
                    value={formData.service_cat_descriptions}
                    onChange={handleChange}
                    isInvalid={!!errors.service_cat_descriptions}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.service_cat_descriptions}
                  </Form.Control.Feedback>
                  <label htmlFor="service_cat_descriptions">Description*</label>
                </Form.Floating>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            type="submit"
            id="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default ServiceCategoryModal;
